import { useAuth } from "../context/AuthContext";
import { useState } from "react";
import { useConsent } from "../context/ConsentContext";
import { PublicSupport } from "./PublicSupport";
import type { AgeGroup } from "../lib/consent/types";
export function PrivacyGate({ children }: { children: React.ReactNode }) {
  const consent = useConsent();
  const { user, signOut } = useAuth();
  const [busy, setBusy] = useState(false);
  const [choice, setChoice] = useState<AgeGroup | "">("");
  const [message, setMessage] = useState("");
  if (consent.isLoading)
    return (
      <main>
        <p role="status">Opening your private session…</p>
        <PublicSupport />
      </main>
    );
  if (!consent.needsAgeVerification && consent.ageGroup !== "under_13")
    return <>{children}</>;
  return (
    <main className="privacy-welcome" id="main-content">
      <h1>Practice at your own pace</h1>
      <p>
        Choose an age range before creating a journal or tracking progress. We
        do not ask for your birthday.
      </p>
      <fieldset>
        <legend>Your age range</legend>
        {(
          [
            ["under_13", "Under 13"],
            ["13_to_17", "13–17"],
            ["18_plus", "18 or older"],
          ] as const
        ).map(([value, label]) => (
          <label key={value}>
            <input
              type="radio"
              name="age-range"
              value={value}
              checked={choice === value}
              disabled={busy}
              onChange={() => {
                setChoice(value);
                setMessage("");
              }}
            />
            {label}
          </label>
        ))}
      </fieldset>
      {choice === "under_13" ? (
        <section aria-labelledby="public-practice-title">
          <h2 id="public-practice-title">You can try a public practice</h2>
          <p>
            Private journals and accounts require verified guardian or school
            enrollment. That enrollment is not available here yet. Do not enter
            private information.
          </p>
          <p>
            Look around and name one neutral object you can see, hear, or feel.
            Take a comfortable normal breath if you want. Stop if uncomfortable
            and ask a trusted adult for support.
          </p>
        </section>
      ) : (
        <>
          <p>
            This guest session stays in this browser tab. People using this open
            tab can see it. Export anything you want to keep before closing; you
            can choose device storage later. Cloud backup and sharing are off
            until you choose them.
          </p>
          <button
            className="btn btn-primary"
            disabled={!choice || busy}
            onClick={async () => {
              if (!choice || busy) return;
              setBusy(true);
              try {
                await consent.setAgeBand(choice);
                await consent.grantConsent("personal_only");
              } catch {
                setMessage("Account enrollment could not be verified. Retry when connected, or use a new guest session without cloud services.");
              } finally { setBusy(false); }
            }}
          >
            Continue with private practice
          </button>
        </>
      )}
      {user && !user.isAnonymous && <button onClick={() => void signOut().catch(() => setMessage("The local session ended; remote sign-out could not be confirmed."))}>Use a new guest session</button>}
      {message && <p role="alert">{message}</p>}
      <PublicSupport />
    </main>
  );
}
